"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import {
  m,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionStyle,
} from "motion/react";
import OrbitImages from "@/components/OrbitImages";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useScrollerRef } from "@/lib/hooks/use-scroller-ref";
import { useTranslation } from "@/lib/hooks/use-translation";

/** The loop, in the order the comp reads it. Each asset is the finished card —
    artwork, label and rounded frame are baked into the webp — so the orbit only
    has to carry it around the ellipse. */
// `labelKey` names the alt text only: the label is baked into the webp, so
// switching language does not redraw the card — it renames it for a screen
// reader, which is the part a translation can reach.
const CARDS = [
  { src: "/v2/OrbitImage/card-visual-1.webp", labelKey: "repeat" },
  { src: "/v2/OrbitImage/card-visual-1-1.webp", labelKey: "know" },
  { src: "/v2/OrbitImage/card-visual-1-2.webp", labelKey: "order" },
  { src: "/v2/OrbitImage/card-visual-1-3.webp", labelKey: "collect" },
  { src: "/v2/OrbitImage/card-visual-1-4.webp", labelKey: "analyse" },
  { src: "/v2/OrbitImage/card-visual-1-5.webp", labelKey: "followUp" },
  { src: "/v2/OrbitImage/card-visual-1-6.webp", labelKey: "points" },
];

/** Scroll spent opening the frame, and then holding it open, on top of the
    pinned viewport. `OPEN_END` is where the opening finishes on the 0 → 1
    progress the two of them share. */
const EXPAND_VH = 90;
const HOLD_VH = 50;
const OPEN_END = EXPAND_VH / (EXPAND_VH + HOLD_VH);

/** One turn of the loop. Slow enough to read a card as it goes past. */
const ORBIT_SECONDS = 42;

/** The panel only pins where the whole thing fits above the fold. Shorter than
    this (landscape phones, a browser buried under toolbars) and the reader
    would be scrolling a pane taller than their screen. */
const PIN_QUERY = "(min-height: 620px)";

/**
 * The type, and the card the section starts as, at each panel size — closed
 * value first where the two differ. `full` is the comp; the other two step it
 * down for panels the comp was never drawn at.
 *
 * These live here rather than in globals.css because the layout maths needs
 * them: where the ring can go on a narrow panel depends on how tall the copy
 * above it ends up. The stylesheet gets them as custom properties and only
 * does the interpolation.
 */
const TIERS = {
  narrow: {
    small: 220,
    title: [20, 24],
    titleLine: [30, 38],
    sub: 17,
    subLine: 26,
    // 426px of text in a 299px panel: it has to wrap here.
    subWidth: "min(501px, 80vw)",
  },
  wide: {
    small: 300,
    title: [26, 32],
    titleLine: [38, 48],
    sub: 20,
    subLine: 28,
    // The comp's 501px is exactly what this line measures at 20px, so asking
    // for 501px rounds into a second line. Let it be as wide as it is.
    subWidth: "max-content",
  },
  full: {
    small: 300,
    title: [32, 40],
    titleLine: [42, 58],
    sub: 20,
    subLine: 28,
    subWidth: "max-content",
  },
} as const;

type Tier = keyof typeof TIERS;

/** Panel width at which the ring can start encircling the copy, and the width
    at which the panel is roomy enough for the comp's own type. */
const RING_AROUND_COPY = 640;
const FULL_SIZE = 1100;

const smoothstep = (x: number) => {
  const t = x < 0 ? 0 : x > 1 ? 1 : x;
  return t * t * (3 - 2 * t);
};

/**
 * CustomerOrbit — v2 section 5: the customer-relationship loop.
 *
 * It starts as a 300px card holding one line of copy, and the scroll opens it
 * out into the full panel: the frame grows from that square to the whole
 * 1248x700 comp, its inset glow travelling with the edge, and what was hidden
 * behind the crop — the subtitle, the tilted ring, the seven cards going round
 * it — comes up as it goes. Once open it holds for `HOLD_VH` before the
 * section lets go of the scroll.
 *
 * The whole animation runs off one number. `--o` (0 = closed, 1 = open) is set
 * on the stage by a single motion value, and every property that moves is a
 * `calc()` against it in globals.css ("Section 5 · customer orbit"). So a frame
 * of this animation costs one custom-property write and no React render at all.
 *
 * What can't be CSS is the ring: `offset-path` wants real pixels. So the panel
 * is measured and `panelLayout` places everything off that — including, on a
 * phone, a different composition altogether (see there).
 */
export default function CustomerOrbit() {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);

  // The page scrolls inside #scroll-container, not the window, so useScroll
  // has to be pointed at it — the same shared ref section 3 uses, which also
  // keeps pointing at the live scrollport after a language switch replaces it
  // (lib/hooks/use-scroller-ref.ts).
  const scrollerRef = useScrollerRef();

  const { scrollYProgress } = useScroll({
    container: scrollerRef,
    target: trackRef,
    offset: ["start start", "end end"],
  });

  /** 0 → 1 as the frame opens, eased the way ScrollExpand eases it. */
  const open = useTransform(scrollYProgress, (p) => smoothstep(p / OPEN_END));

  const reduceMotion = useReducedMotion();
  const canPin = useMediaQuery(PIN_QUERY);
  const pinned = canPin && !reduceMotion;

  // The ring is laid out in pixels, so the panel has to be measured. One state
  // update per resize, never per frame.
  const [panel, setPanel] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setPanel((prev) => {
        const w = Math.round(width);
        const h = Math.round(height);
        return prev.w === w && prev.h === h ? prev : { w, h };
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // How tall the copy ends up is a wrapping question, and only the browser can
  // answer it. Counting *lines* rather than pixels is what makes it safe to
  // read mid-animation: the headline's height changes as the type grows, but
  // its height over its own line-height doesn't.
  //
  // Deliberately no dependency array. It has to re-check after any render that
  // could have re-wrapped the copy, and it settles because an unchanged count
  // returns the previous state and React bails out.
  const [lines, setLines] = useState({ title: 2, sub: 2 });
  // The missing dependency list is the point, and the guarded setState is what
  // makes it terminate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const title = countLines(titleRef.current);
    const sub = countLines(subRef.current);
    if (!title || !sub) return;
    setLines((prev) =>
      prev.title === title && prev.sub === sub ? prev : { title, sub },
    );
  });

  // Unmeasured, everything falls back to the middle tier. The section is far
  // below the fold, so the one frame before the observer reports is never on
  // screen, and this way the server renders a sensible panel rather than a
  // phone layout on a desktop.
  const layout =
    panel.w && panel.h
      ? panelLayout(panel.w, panel.h, lines.title, lines.sub)
      : null;
  const tier: Tier = layout?.tier ?? "wide";
  const type = TIERS[tier];

  return (
    <section aria-label={t("home.customerOrbit.aria")} className="bg-[#FCFCFC]">
      <div
        ref={trackRef}
        className="relative"
        style={
          pinned
            ? { height: `calc(100dvh + ${EXPAND_VH + HOLD_VH}vh)` }
            : undefined
        }
      >
        {/* top-13 matches the scroll container's pt-13: the fixed navbar sits
            over the first 3.25rem of the scrollport, so that's where a pinned
            pane has to stop. */}
        <div
          className={
            pinned
              ? "sticky top-13 flex h-[calc(100dvh-3.25rem)] items-center justify-center px-4 md:px-[80px]"
              : "flex items-center justify-center px-4 py-12 md:px-[80px]"
          }
        >
          <m.div
            ref={stageRef}
            className="orbit-stage relative h-[clamp(420px,calc(100dvh-9rem),700px)] w-full max-w-[1248px]"
            style={
              {
                // The one value the whole section animates off. Pinned, it
                // comes from the scroll; unpinned (or reduced motion), the
                // panel is simply already open.
                "--o": pinned ? open : 1,
                // Sizes come from the panel we measured rather than from a
                // media query: `md:px-[80px]` means the panel is *wider* at
                // 767px than at 768px, so a viewport breakpoint would disagree
                // with the ring exactly where it matters.
                "--orbit-small": `${type.small}px`,
                "--title-0": `${type.title[0]}px`,
                "--title-1": `${type.title[1]}px`,
                "--title-line-0": `${type.titleLine[0]}px`,
                "--title-line-1": `${type.titleLine[1]}px`,
                "--sub": `${type.sub}px`,
                "--sub-line": `${type.subLine}px`,
                "--sub-width": type.subWidth,
                "--copy-shift": `${layout?.copyShift ?? 0}px`,
              } as unknown as MotionStyle
            }
          >
            {/* The frame. Grows from --orbit-small to the full stage, and the
                inset glow rides its edge because the glow is on the box that
                is actually changing size — not on a full-size box being
                clipped, where the glow would sit off past the crop. */}
            <div className="orbit-frame absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[32px] bg-white">
              {/* Everything inside is laid out against the *stage*, centred on
                  the frame's centre, so the contents hold still and the frame
                  opens over them rather than dragging them along. */}
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ width: panel.w || "100%", height: panel.h || "100%" }}
              >
                {layout ? (
                  <div
                    className="orbit-ring absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
                    style={{
                      top: layout.centerY,
                      width: layout.box,
                      height: layout.box,
                    }}
                  >
                    <OrbitImages
                      items={CARDS.map((card) => (
                        <Image
                          key={card.src}
                          src={card.src}
                          alt={t(`home.customerOrbit.labels.${card.labelKey}`)}
                          width={560}
                          height={560}
                          sizes={`${Math.round(layout.itemSize)}px`}
                          draggable={false}
                          className="h-full w-full object-contain"
                        />
                      ))}
                      shape="ellipse"
                      baseWidth={layout.box}
                      width={layout.box}
                      height={layout.box}
                      radiusX={layout.rx}
                      radiusY={layout.ry}
                      itemSize={layout.itemSize}
                      rotation={-3}
                      duration={ORBIT_SECONDS}
                      showPath
                      pathColor="#B0D9F9"
                      pathWidth={3}
                      paused={!!reduceMotion}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {/* The copy sits above the frame rather than inside it: closed, the
                headline is wider than the 300px box and spills past it, which
                is what the comp shows. The subtitle hangs out of flow beneath
                the headline, so on a wide panel the headline's centre never
                moves and the type simply grows in place. On a narrow one the
                whole block rides up with `--o` to clear the ring. */}
            <div className="pointer-events-none absolute inset-0 grid place-items-center px-4">
              <div className="orbit-copy relative">
                <h2
                  ref={titleRef}
                  className="orbit-title text-center font-semibold text-black [text-wrap:balance]"
                >
                  {t("home.customerOrbit.heading.a")}
                  <br />
                  {t("home.customerOrbit.heading.b")}
                </h2>
                <p
                  ref={subRef}
                  className="orbit-sub absolute left-1/2 top-full mt-2 max-w-[calc(100vw-2rem)] -translate-x-1/2 text-center text-black/60 [text-wrap:balance]"
                >
                  {t("home.customerOrbit.sub")}
                </p>
              </div>
            </div>
          </m.div>
        </div>
      </div>
    </section>
  );
}

/** How many lines an element's text currently occupies. Scale-invariant, so it
    is the same answer whether it is read closed, open or halfway. */
function countLines(el: HTMLElement | null) {
  if (!el) return 0;
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
  if (!lineHeight) return 0;
  return Math.max(1, Math.round(el.getBoundingClientRect().height / lineHeight));
}

/** Gap between the copy and the ring, and the panel's own breathing room, at
    the point where the narrow layout is at its tightest. */
const MIN_GAP = 20;

/**
 * Where everything goes, in pixels, for the panel we measured.
 *
 * Wide is the comp: the copy in the middle and the ring around it, at radii of
 * 41% and 24.1% of the panel — which clears the copy above and below and keeps
 * the cards inside the frame at the sides.
 *
 * Narrow can't have that. Once the headline wraps it is as wide as the panel,
 * so no ellipse both encircles it and fits — and one drawn wider than the panel
 * just parks half-cards on the frame's edge. So the composition changes rather
 * than being crushed: the copy rides up to the top of the panel as it opens
 * (`copyShift`, spent against `--o`) and the ring takes the room underneath.
 * Still a full loop, still nothing cropped. The three gaps it leaves — above
 * the copy, between copy and ring, below the ring — are equal, so the panel
 * stays balanced from a tall phone down to a short one.
 *
 * `box` is the square the path is drawn in. OrbitImages centres its path at
 * `baseWidth / 2` and its `showPath` SVG clips to that box, so the square has
 * to hold the whole ellipse plus a card's width of overhang.
 */
function panelLayout(w: number, h: number, titleLines: number, subLines: number) {
  if (w >= RING_AROUND_COPY) {
    const tier: Tier = w >= FULL_SIZE ? "full" : "wide";
    // 560px assets with transparent margin around the card: the card itself is
    // ~90% of the frame, so an item box is the comp's 120px divided by that.
    const itemSize = Math.min(133, w * 0.107);
    // The copy is anchored by the headline's centre, so the subtitle hanging
    // below it puts the block's optical centre lower than the panel's. The
    // ring drops by half that overhang, which is what centres the copy inside
    // it rather than merely inside the frame.
    const centerY = h / 2 + (8 + subLines * TIERS[tier].subLine) / 2;
    // A touch wider and taller than the comp's 41% / 24.1%, held off the
    // frame's edge by MIN_GAP so the cards never graze it on a smaller panel.
    const rx = Math.min(w * 0.435, w / 2 - itemSize / 2 - MIN_GAP);
    const ry = Math.min(
      h * 0.27,
      Math.min(centerY, h - centerY) - itemSize / 2 - MIN_GAP,
    );
    return { tier, itemSize, rx, ry, centerY, copyShift: 0, box: box(rx, ry, itemSize) };
  }

  const type = TIERS.narrow;
  const copyH =
    titleLines * type.titleLine[1] + 8 + subLines * type.subLine;

  // 15.5% of the panel, from the comp's 51.5px card on a ~350px frame. The
  // cards have to come down with the ellipse: the flat shape has a much
  // shorter perimeter than the tall one it replaced, and a card sized for that
  // one leaves seven of them shoulder to shoulder around this.
  const wanted = Math.max(48, Math.min(104, w * 0.155));
  const rx = w / 2 - wanted / 2 - 8;
  // The phone comp draws the same wide, flat ellipse the desktop panel does —
  // 308 x 83 — only smaller, rather than a ring stretched tall to fill a
  // narrow column: the shape is the section's signature and should read the
  // same at both sizes. A little rounder than the comp's own 0.27 by request,
  // which also buys the seven cards more arc to sit on. The room term still
  // has the final say, since it is what guarantees `2 * ry + wanted` fits
  // under the copy.
  const ry = Math.max(
    wanted * 0.3,
    Math.min(rx * 0.38, (h - copyH - 2 * MIN_GAP - wanted) / 2),
  );
  // On a squashed ellipse the cards would start overlapping each other, so the
  // card gives way before the ring does.
  const itemSize = Math.min(wanted, (perimeter(rx, ry) / CARDS.length) * 0.92);

  const ringH = 2 * ry + itemSize;
  const gap = Math.max(0, h - copyH - ringH) / 3;

  return {
    tier: "narrow" as Tier,
    itemSize,
    rx,
    ry,
    centerY: gap + copyH + gap + ringH / 2,
    // The headline's centre, which is what the copy block is anchored by.
    copyShift: h / 2 - (gap + (titleLines * type.titleLine[1]) / 2),
    box: box(rx, ry, itemSize),
  };
}

const box = (rx: number, ry: number, itemSize: number) =>
  Math.ceil(2 * Math.max(rx, ry) + itemSize + 8);

/** Ramanujan's approximation — good to a fraction of a percent, and all this
    needs is "is there room for seven cards on here". */
const perimeter = (a: number, b: number) =>
  Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
